// ProfileModal — thin shell that composes the profile modal from sub-components.
'use client';

import { User, Lock, Bell } from 'lucide-react';
import { clsx } from 'clsx';
import { useProfileModal } from './profile-modal/useProfileModal';
import { PROFILE_TABS } from './profile-modal/profile-modal-helpers';
import type { ProfileTab } from './profile-modal/profile-modal-helpers';
import ProfileModalBody from './profile-modal/ProfileModalBody';
import Modal from '../ui/Modal';
import FormFooter from '../ui/FormFooter';

/** Props for the ProfileModal component. */
export interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Icon lookup for each tab. */
const TAB_ICONS: Record<ProfileTab, typeof User> = {
  profile: User,
  security: Lock,
  notifications: Bell,
};

/**
 * Modal for viewing and editing the current user's profile, password, and notification preferences.
 * @param props - Controls modal visibility and provides a close callback
 */
export default function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const state = useProfileModal({ isOpen, onClose });

  // BL-1230: each tab owns its own save status. The notifications tab saves
  // through its own handler and its own state, so reading `state.success` for
  // every tab would leave a successful opt-in save silently unconfirmed.
  const successMessage =
    state.activeTab === 'notifications' ? state.notificationsSuccess : state.success;

  // The tab's Save, if it has one. BL-1230: the notifications tab had NO save
  // button before — its toggles were cosmetic. It is hidden when every row is locked
  // for the user's role (BUG-2272), so the tab doesn't offer a Save that writes nothing.
  const save =
    state.activeTab === 'profile'
      ? { run: state.handleSaveProfile, busy: state.isSaving }
      : state.activeTab === 'notifications' && state.notifications.some((p) => !p.locked)
        ? { run: state.handleSaveNotifications, busy: state.isSavingNotifications }
        : null;

  return (
    // Modal's chrome draws the panel, the one white header (BL-2692), the
    // scrolling body and the footer row.
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="My profile"
      testId="profile-modal"
      size="2xl"
      headerDensity="form"
      title="My profile"
      footer={
        <FormFooter
          onCancel={onClose}
          onSubmit={save?.run}
          showSubmit={save != null}
          submitLabel="Save changes"
          isSubmitting={save?.busy ?? false}
        />
      }
    >
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        {PROFILE_TABS.map((tab) => {
          const Icon = TAB_ICONS[tab.id];
          return (
            <button
              type="button"
              key={tab.id}
              onClick={() => state.setActiveTab(tab.id)}
              className={clsx(
                'flex items-center px-6 py-3 text-body font-medium transition-colors border-b-2 -mb-px',
                state.activeTab === tab.id
                  ? 'text-primary border-secondary'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              )}
            >
              <Icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <ProfileModalBody
        activeTab={state.activeTab}
        isLoading={state.isLoading}
        error={state.error}
        success={successMessage}
        profile={state.profile}
        notifications={state.notifications}
        isLoadingNotifications={state.isLoadingNotifications}
        notificationsError={state.notificationsError}
        passwords={state.passwords}
        showPasswords={state.showPasswords}
        isSaving={state.isSaving}
        form={state.form}
        onToggleNotification={state.handleToggleNotification}
        onPasswordsChange={state.setPasswords}
        onShowPasswordsChange={state.setShowPasswords}
        onChangePassword={state.handleChangePassword}
      />
    </Modal>
  );
}
